//import external libraries
import React, { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

//import internal resources and services
import config from "../../utils/config";
import { editProject } from "../../utils/services/projectService";
import { getProject } from "../../utils/services/projectService";
import {
  calculateDewPoint,
  calculateWetBulbTemperature,
  findHumidityRatio,
} from "../../utils/calculationFunctions";
import { setSnackbar } from "../../actions/snackbarActions";
import { themeCtx } from "../../utils/hooks/themeContext";
import Customer from "./Customer";

//import MUI components
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";

function EditProject() {
  const location = useLocation();
  const projId = location.state.projId;

  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState({
    jwt: localStorage.getItem(config.jwt_key_name),
    id: projId,
    number: "AHU1",
    costDriver: "",
    name: "",
    country: "",
    address: "",
    //summer
    summerOutdoorTemperature: 0,
    summerWetBulbTemperature: 0,
    summerDewPointTemperature: 0,
    summerOutdoorHumidity: 0,
    //winter
    winterOutdoorTemperature: 0,
    winterOutdoorHumidityRatio: 0,
    elevation: 0,
    winterOutdoorHumidity: 0,
    //electricity
    elPriceBusiness: 0,
    elPriceHome: 0,
    status: "",
    //Weather
    aqiCurrent: 0,
    aqiYearlyAverage: 0,
    aqiQualityGood: 0,
    aqiQualityModerate: 0,
    aqiQualitySensitive: 0,
    aqiQualityUnhealthy: 0,
    aqiQualityUnhealthyV: 0,
    aqiQualityHazardous: 0,
    distanceTemperature: 0,
    distance: 0,
    totalPrice: 0,
    timeZone: "",
    ERPId: "",
    customerName: "",
    customerRepresentative: "",
    customerPhone: "",
    customerEmail: "",
  });
  const [initialValues, setInitialValues] = useState({
    jwt: localStorage.getItem(config.jwt_key_name),
    number: "AHU1",
    costDriver: "22R&D0006",
  });
  const [name, setName] = useState(false);
  const [elevation, setElevation] = useState(false);
  const [summerOutdoorTemperature, setSummerOutdoorTemperature] =
    useState(false);
  const [summerWetBulbTemperature, setSummerWetBulbTemperature] =
    useState(false);
  const [summerDewPointTemperature, setSummerDewPointTemperature] =
    useState(false);
  const [summerOutdoorHumidity, setSummerOutdoorHumidity] = useState(false);
  const [winterOutdoorTemperature, setWinterOutdoorTemperature] =
    useState(false);
  const [winterOutdoorHumidity, setWinterOutdoorHumidity] = useState(false);
  const [elPriceBusiness, setElPriceBusiness] = useState(false);
  const [elPriceHome, setElPriceHome] = useState(false);
  const [aqiCurrent, setAqiCurrent] = useState(false);
  const [aqiYearlyAverage, setAqiYearlyAverage] = useState(false);
  const [showCustomerData, setShowCustomerData] = useState(false);

  const { t } = useTranslation();

  const theme = useTheme();
  const { currentTheme } = useContext(themeCtx);
  const dispatch = useDispatch();

  useEffect(() => {
    getProject(projId).then((response) => {
      if (response.statusCode >= 200 && response.statusCode < 300) {
        setProject({
          ...response.data,
          markerPosition: {
            lat: response.data.latitude,
            lng: response.data.longitude,
          },
          ERPId: response.data.customer.erpId
            ? response.data.customer.erpId
            : "",
          customerName: response.data.customer.name
            ? response.data.customer.name
            : "",
          customerRepresentative: response.data.customer.representative
            ? response.data.customer.representative
            : "",
          customerPhone: response.data.customer.phone
            ? response.data.customer.phone
            : "",
          customerEmail: response.data.customer.email
            ? response.data.customer.email
            : "",
          CustomerId: response.data.customer.id
            ? response.data.customer.id
            : "",
        });
        setInitialValues(response.data);
      }
    });
  }, []);

  const areMandatoryFieldsFilled = (proj) => {
    if (
      !proj.name ||
      !proj.summerOutdoorTemperature ||
      !proj.summerWetBulbTemperature ||
      !proj.summerDewPointTemperature ||
      !proj.summerOutdoorHumidity ||
      !proj.aqiCurrent ||
      !proj.elPriceBusiness ||
      !proj.elPriceHome ||
      !proj.winterOutdoorTemperature ||
      !proj.winterOutdoorHumidity ||
      !proj.aqiYearlyAverage
    )
      return false;
    return true;
  };
  function validateInput(name, value, minValue, maxValue) {
    let newValue;
    if (value !== "" && value !== "-") {
      if (parseFloat(value) > minValue && parseFloat(value) < maxValue)
        newValue = value;
      else {
        newValue = initialValues[name];
        dispatch(
          setSnackbar(
            true,
            "error",
            t(
              "createproject.validation" + name[0].toUpperCase() + name.slice(1)
            )
          )
        );
      }
    } else newValue = value;
    return newValue;
  }
  const validate = () => {
    let validationFailed = false;

    if (project.name.length > 0 && project.name.length < 51) {
      if (!/[\\/:\*\?"<>\|]+/.test(project.name)) {
        setName(false);
      } else {
        setName(true);
        validationFailed = true;
      }
    } else {
      setName(true);
      validationFailed = true;
    }
    if (project.elevation !== "") {
      setElevation(false);
    } else {
      setElevation(true);
      validationFailed = true;
    }

    if (
      project.summerOutdoorTemperature !== "" &&
      project.summerOutdoorTemperature != "-"
    ) {
      setSummerOutdoorTemperature(false);
    } else {
      setSummerOutdoorTemperature(true);
      validationFailed = true;
    }

    if (project.summerWetBulbTemperature !== "") {
      setSummerWetBulbTemperature(false);
    } else {
      setSummerWetBulbTemperature(true);
      validationFailed = true;
    }

    if (project.summerDewPointTemperature !== "") {
      setSummerDewPointTemperature(false);
    } else {
      setSummerDewPointTemperature(true);
      validationFailed = true;
    }

    if (project.summerOutdoorHumidity !== "") {
      setSummerOutdoorHumidity(false);
    } else {
      setSummerOutdoorHumidity(true);
      validationFailed = true;
    }

    if (project.elPriceBusiness != "") {
      setElPriceBusiness(false);
    } else {
      setElPriceBusiness(true);
      validationFailed = true;
    }

    if (project.elPriceHome != "") {
      setElPriceHome(false);
    } else {
      setElPriceHome(true);
      validationFailed = true;
    }

    if (project.aqiCurrent !== "") {
      setAqiCurrent(false);
    } else {
      setAqiCurrent(true);
      validationFailed = true;
    }

    if (project.winterOutdoorTemperature !== "") {
      setWinterOutdoorTemperature(false);
    } else {
      setWinterOutdoorTemperature(true);
      validationFailed = true;
    }

    if (project.winterOutdoorHumidity !== "") {
      setWinterOutdoorHumidity(false);
    } else {
      setWinterOutdoorHumidity(true);
      validationFailed = true;
    }

    if (project.aqiYearlyAverage !== "") {
      setAqiYearlyAverage(false);
    } else {
      setAqiYearlyAverage(true);
      validationFailed = true;
    }
    validationFailed &&
      dispatch(setSnackbar(true, "error", t("snackbar.createProjectError")));
    return validationFailed;
  };
  const handleFocus = (event) => {
    event.target.select();
  };

  const editProj = (e) => {
    if (!areMandatoryFieldsFilled(project)) {
      dispatch(setSnackbar(true, "info", t("snackbar.mandatoryFields")));
    }

    const checkValidity = validate();

    if (checkValidity) return;
    setLoading(true);

    const requestData = {
      ...project,
      localDateTime: new Date(),
    };

    editProject(requestData)
      .then((response) => {
        if (response.statusCode >= 200 && response.statusCode < 300) {
          dispatch(
            setSnackbar(
              true,
              "success",
              t("snackbar.editProjectSuccess"),
              "/project-item",
              { state: { projectId: projId } }
            )
          );
          setLoading(false);
        } else {
          dispatch(setSnackbar(true, "error", t("snackbar.editProjectError")));
          setLoading(false);
        }
      })
      .catch((err) => {
        dispatch(setSnackbar(true, "error", t("snackbar.editProjectError")));
        setLoading(false);
      });
  };
  const handleProjectChange = (e) => {
    let { name, value } = e.target;

    if (
      name === "summerOutdoorTemperature" ||
      name === "winterOutdoorTemperature"
    ) {
      let newValue = validateInput(name, value, -31, 56);

      setProject((prev) => {
        return {
          ...prev,
          [name]: newValue,
        };
      });
    } else if (
      name === "summerOutdoorHumidity" ||
      name === "winterOutdoorHumidity"
    ) {
      let newValue = validateInput(name, value, -1, 101);

      setProject((prev) => {
        return {
          ...prev,
          [name]: newValue,
        };
      });
    } else {
      setProject((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };

  useEffect(() => {
    //kalkulacija polja za wet bulb i dew point na osnovu dry bulb i vlaznosti

    let wetBulbTemperature = calculateWetBulbTemperature(
      project.summerOutdoorTemperature,
      project.summerOutdoorHumidity
    );
    let dewPointTemperature = calculateDewPoint(
      +project.summerOutdoorTemperature,
      +project.summerOutdoorHumidity
    );

    setProject((prev) => {
      return {
        ...prev,
        summerWetBulbTemperature: wetBulbTemperature,
        summerDewPointTemperature: dewPointTemperature,
      };
    });
  }, [project.summerOutdoorTemperature, project.summerOutdoorHumidity]);

  useEffect(() => {
    let humidityRatio = findHumidityRatio(
      +project.winterOutdoorTemperature,
      +project.winterOutdoorHumidity,
      +project.elevation
    );

    setProject((prev) => {
      return {
        ...prev,
        winterOutdoorHumidityRatio: humidityRatio,
      };
    });
  }, [
    project.winterOutdoorTemperature,
    project.winterOutdoorHumidity,
    project.elevation,
  ]);

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Paper
        variant="outlined"
        sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
      >
        <Backdrop
          sx={{
            color: theme.palette.primary.light,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loading}
        >
          <CircularProgress />
        </Backdrop>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label={t("createproject.projectname")}
            name="name"
            autoFocus
            value={project.name}
            onChange={(e) => {
              setName(false);
              handleProjectChange(e);
            }}
            type="text"
            helperText={name ? `${t("createproject.validationName")}` : ""}
            error={name}
          />
          {currentTheme && (
            <Customer
              showCustomerData={showCustomerData}
              setShowCustomerData={setShowCustomerData}
              handleChange={handleProjectChange}
              ERPId={project.ERPId}
              customerName={project.customerName}
              customerRepresentative={project.customerRepresentative}
              customerPhone={project.customerPhone}
              customerEmail={project.customerEmail}
            ></Customer>
          )}
          <TextField
            margin="normal"
            disabled
            fullWidth
            id="address"
            label={t("createproject.location")}
            name="address"
            autoComplete={t("createproject.location")}
            value={
              (project.address != "" ? project.address + ", " : "") +
              project.city +
              ", " +
              project.country
            }
          />
          <TextField
            margin="normal"
            fullWidth
            id="elevation"
            label={t("createproject.elevation")}
            name="elevation"
            autoComplete={t("createproject.elevation")}
            value={project.elevation}
            onChange={(e) => {
              setElevation(false);
              handleProjectChange(e);
            }}
            type="number"
            inputType="number"
            helperText={elevation ? `${t("createproject.textnumber")}` : ""}
            error={elevation}
            InputProps={{
              endAdornment: <InputAdornment position="start">m</InputAdornment>,
              onWheel: (e) => e.target.blur(),
            }}
          />

          <Paper
            variant="outlined"
            sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
          >
            <Box>
              <header>
                <Typography component="h2" variant="h5">
                  {t("createproject.summer")}
                </Typography>
              </header>

              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="summerOutdoorTemperature"
                  label={t("createproject.dryBulbTemperatureCelsius")}
                  name="summerOutdoorTemperature"
                  autoComplete={t("createproject.dryBulbTemperatureCelsius")}
                  value={project.summerOutdoorTemperature}
                  onChange={(e) => {
                    setSummerOutdoorTemperature(false);
                    handleProjectChange(e);
                  }}
                  onFocus={handleFocus}
                  inputType="number"
                  type="number"
                  inputProps={{
                    step: 0.1,
                  }}
                  helperText={
                    summerOutdoorTemperature
                      ? `${t(
                          "createproject.validationSummerOutdoorTemperature"
                        )}`
                      : ""
                  }
                  error={summerOutdoorTemperature}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
                <TextField
                  margin="normal"
                  disabled
                  fullWidth
                  id="summerWetBulbTemperature"
                  label={t("createproject.wetBulbTemperatureCelsius")}
                  name="summerWetBulbTemperature"
                  autoComplete={t("createproject.wetBulbTemperatureCelsius")}
                  value={project.summerWetBulbTemperature}
                  onChange={(e) => {
                    setSummerWetBulbTemperature(false);
                    handleProjectChange(e);
                  }}
                  inputType="number"
                  type="number"
                  inputProps={{
                    step: 0.1,
                  }}
                  helperText={
                    summerWetBulbTemperature
                      ? `${t(
                          "createproject.validationSummerWetBulbTemperature"
                        )}`
                      : ""
                  }
                  error={summerWetBulbTemperature}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />

                <TextField
                  margin="normal"
                  disabled
                  fullWidth
                  id="dewPointTemperatureCelsius"
                  label={t("createproject.dewPointTemperatureCelsius")}
                  name="summerDewPointTemperature"
                  autoComplete={t("createproject.dewPointTemperatureCelsius")}
                  value={project.summerDewPointTemperature}
                  onChange={(e) => {
                    setSummerDewPointTemperature(false);
                    handleProjectChange(e);
                  }}
                  inputType="number"
                  type="number"
                  helperText={
                    summerDewPointTemperature
                      ? `${t(
                          "createproject.validationSummerDewPointTemperature"
                        )}`
                      : ""
                  }
                  error={summerDewPointTemperature}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  id="summerOutdoorHumidity"
                  label={t("createproject.relativeHumiditySummer")}
                  name="summerOutdoorHumidity"
                  autoComplete={t("createproject.relativeHumiditySummer")}
                  value={project.summerOutdoorHumidity}
                  onChange={(e) => {
                    setSummerOutdoorHumidity(false);
                    handleProjectChange(e);
                  }}
                  onFocus={handleFocus}
                  type="number"
                  inputType="number"
                  helperText={
                    summerOutdoorHumidity
                      ? `${t("createproject.validationSummerOutdoorHumidity")}`
                      : ""
                  }
                  error={summerOutdoorHumidity}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
                <Typography component="h2" variant="h5">
                  {t("createproject.winter")}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="winterOutdoorTemperature"
                  label={t("createproject.heatingDB")}
                  name="winterOutdoorTemperature"
                  autoComplete={t("createproject.heatingDB")}
                  value={project.winterOutdoorTemperature}
                  onChange={(e) => {
                    setWinterOutdoorTemperature(false);
                    handleProjectChange(e);
                  }}
                  onFocus={handleFocus}
                  type="number"
                  inputType="number"
                  inputProps={{
                    step: 0.1,
                  }}
                  helperText={
                    winterOutdoorTemperature
                      ? `${t(
                          "createproject.validationWinterOutdoorTemperature"
                        )}`
                      : ""
                  }
                  error={winterOutdoorTemperature}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
                <TextField
                  margin="normal"
                  disabled
                  fullWidth
                  id="humidityRatio"
                  label={t("createproject.humidityRatio")}
                  name="winterOutdoorHumidityRatio"
                  autoComplete={t("createproject.humidityRatio")}
                  value={project.winterOutdoorHumidityRatio}
                  onChange={(e) => {
                    //dodaj validaciju
                    handleProjectChange(e);
                  }}
                  inputProps={{
                    step: 0.1,
                  }}
                  type="number"
                  inputType="number"
                  //helperText={heatingDB1 ? `${t('createproject.textnumber')}` : ""}
                  //error={ heatingDB1 }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">g/g</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />

                <TextField
                  margin="normal"
                  fullWidth
                  id="winterOutdoorHumidity"
                  label={t("createproject.relativeHumidityWinter")}
                  name="winterOutdoorHumidity"
                  autoComplete={t("createproject.relativeHumidityWinter")}
                  value={project.winterOutdoorHumidity}
                  onChange={(e) => {
                    setWinterOutdoorHumidity(false);
                    handleProjectChange(e);
                  }}
                  onFocus={handleFocus}
                  type="number"
                  inputType="number"
                  helperText={
                    winterOutdoorHumidity
                      ? `${t("createproject.validationWinterOutdoorHumidity")}`
                      : ""
                  }
                  error={winterOutdoorHumidity}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              </Box>
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
          >
            <Typography
              component="h2"
              variant="h5"
              sx={{ marginBottom: "15px" }}
            >
              {t("createproject.utilityrates")}
            </Typography>

            <Box sx={{ display: "flex" }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="elPriceBusiness"
                label={t("createproject.electricity")}
                name="elPriceBusiness"
                autoComplete={t("createproject.electricity")}
                value={project.elPriceBusiness}
                onChange={(e) => {
                  setElPriceBusiness(false);
                  handleProjectChange(e);
                }}
                type="number"
                inputType="number"
                inputProps={{
                  step: 0.001,
                }}
                helperText={
                  elPriceBusiness
                    ? `${t("createproject.validationRequired")}`
                    : ""
                }
                error={elPriceBusiness}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">€/kWh</InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
                sx={{ width: "50%", m: 1 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="elPriceHome"
                label={t("createproject.electricityhome")}
                name="elPriceHome"
                autoComplete={t("createproject.electricityhome")}
                value={project.elPriceHome}
                onChange={(e) => {
                  setElPriceHome(false);
                  handleProjectChange(e);
                }}
                type="number"
                inputType="number"
                inputProps={{
                  step: 0.001,
                }}
                helperText={
                  elPriceHome ? `${t("createproject.validationRequired")}` : ""
                }
                error={elPriceHome}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">€/kWh</InputAdornment>
                  ),

                  onWheel: (e) => e.target.blur(),
                }}
                sx={{ width: "50%", m: 1 }}
              />
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, align: "center" }}
          >
            <Box>
              <header>
                <Typography
                  component="h4"
                  variant="h6"
                  sx={{ marginBottom: "15px" }}
                >
                  {t("createproject.airquality")}
                </Typography>
              </header>
              <Box>
                <Box sx={{ display: "flex" }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="aqiCurrent"
                    label={t("createproject.aqi")}
                    name="aqiCurrent"
                    autoComplete={t("createproject.aqi")}
                    value={project.aqiCurrent}
                    onChange={(e) => {
                      setAqiCurrent(false);
                      handleProjectChange(e);
                    }}
                    inputType="number"
                    InputProps={{
                      onWheel: (e) => e.target.blur(),
                    }}
                    type="number"
                    helperText={
                      aqiCurrent
                        ? `${t("createproject.validationRequired")}`
                        : ""
                    }
                    error={aqiCurrent}
                    sx={{ width: "50%", m: 1 }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="aqiYearlyAverage"
                    label={t("createproject.aqiAvg")}
                    name="aqiYearlyAverage"
                    autoComplete={t("createproject.aqiAvg")}
                    value={project.aqiYearlyAverage}
                    onChange={(e) => {
                      setAqiYearlyAverage(false);
                      handleProjectChange(e);
                    }}
                    inputType="number"
                    InputProps={{
                      onWheel: (e) => e.target.blur(),
                    }}
                    type="number"
                    helperText={
                      aqiYearlyAverage
                        ? `${t("createproject.validationRequired")}`
                        : ""
                    }
                    error={aqiYearlyAverage}
                    sx={{ width: "50%", m: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              background: theme.palette.secondary.main,
              "&:hover": { background: theme.palette.secondary.main },
            }}
            onClick={editProj}
          >
            {t("createproject.editbtn")}
          </Button>
          <Typography variant="body2">
            {t("createproject.requiredField")}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default EditProject;
